/**
 * WowStore Live Studio — VJ Compositor
 * =====================================
 * Browser-side visual layer compositor for WowStore Live.
 * Driven by Socket.io `vj:*` events from the Show Director.
 *
 * Layers:
 *   L0  Background   — gradients, solid, pulse, themed, video
 *   L1  Lucy         — video element with position/size/clip switching
 *   L2  Product      — side-panel, fullscreen, video, carousel
 *   L3  Overlays     — lower-thirds, price tags, badges, countdowns, banners, confetti
 *   L4  Interactive  — polls, chat highlights (managed externally)
 *   L5  Transitions  — CSS animations between states
 *
 * Exposes: window.WowVJ
 * Requires: Socket.io connection on window (LucyClubhouse or WowLiveEngine)
 *
 * All dynamic text is escaped via escapeHTML/escapeAttr before DOM insertion.
 * Data originates from the trusted Show Director server only.
 *
 * (c) 2026 WowStore. Production module.
 */
;(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // 0. Constants
  // ---------------------------------------------------------------------------

  var TRANSITION_MS = 800;
  var LAYER_Z_BASE  = 40; // above existing overlays (10-30)

  var LUCY_DEFAULT_CLIP =
    'https://cdn.shopify.com/s/files/1/0878/9042/8232/t/17/assets/lucy-waving.mp4?v=1772706969';
  var LUCY_CDN_BASE   = 'https://ai-api.wowstore.live/static/lucy/';
  var AUDIO_CDN_BASE  = 'https://ai-api.wowstore.live/static/audio/';

  var CONFETTI_COUNT  = 60;
  var COUNTDOWN_TICK  = 1000;

  // SFX presets — short file names resolved against AUDIO_CDN_BASE
  var SFX_MAP = {
    ding:          'ding.mp3',
    'confetti-pop':'confetti-pop.mp3',
    alert:         'alert.mp3',
    applause:      'applause.mp3',
    whoosh:        'whoosh.mp3',
    chime:         'chime.mp3',
    cash:          'cash-register.mp3'
  };

  // ---------------------------------------------------------------------------
  // 1. CSS Injection
  // ---------------------------------------------------------------------------

  var CSS = [
    '/* === WowVJ Compositor Styles === */',

    // Layer base
    '.vj-layer {',
    '  position: absolute;',
    '  top: 0; left: 0; width: 100%; height: 100%;',
    '  pointer-events: none;',
    '  transition: all ' + TRANSITION_MS + 'ms ease-in-out;',
    '}',

    // L0 Background
    '#vj-l0-bg {',
    '  z-index: ' + LAYER_Z_BASE + ';',
    '}',
    '#vj-l0-bg.vj-pulse {',
    '  animation: vjPulse 2s ease-in-out infinite alternate;',
    '}',
    '@keyframes vjPulse {',
    '  0%   { filter: brightness(1); }',
    '  100% { filter: brightness(1.25); }',
    '}',
    '#vj-l0-video {',
    '  width: 100%; height: 100%; object-fit: cover;',
    '}',

    // L1 Lucy
    '#vj-l1-lucy {',
    '  z-index: ' + (LAYER_Z_BASE + 1) + ';',
    '  display: flex;',
    '  align-items: center;',
    '  justify-content: center;',
    '}',
    '#vj-l1-lucy.vj-pos-center  { align-items: center; justify-content: center; }',
    '#vj-l1-lucy.vj-pos-left    { justify-content: flex-start; padding-left: 5%; }',
    '#vj-l1-lucy.vj-pos-right   { justify-content: flex-end; padding-right: 5%; }',
    '#vj-l1-lucy.vj-pos-pip-bottom-right {',
    '  align-items: flex-end; justify-content: flex-end;',
    '  padding: 4% 4%;',
    '}',
    '#vj-l1-lucy.vj-pos-pip-bottom-left {',
    '  align-items: flex-end; justify-content: flex-start;',
    '  padding: 4% 4%;',
    '}',
    '#vj-lucy-video {',
    '  border-radius: 12px;',
    '  object-fit: contain;',
    '  max-width: 80%; max-height: 90%;',
    '  transition: all ' + TRANSITION_MS + 'ms ease-in-out;',
    '}',

    // L2 Product
    '#vj-l2-product {',
    '  z-index: ' + (LAYER_Z_BASE + 2) + ';',
    '  display: flex;',
    '  align-items: center;',
    '  justify-content: flex-end;',
    '}',
    '#vj-product-panel {',
    '  background: rgba(255,255,255,0.97);',
    '  border-radius: 16px 0 0 16px;',
    '  box-shadow: -4px 0 24px rgba(0,0,0,0.15);',
    '  width: 38%; max-height: 88%;',
    '  overflow: hidden;',
    '  transition: all ' + TRANSITION_MS + 'ms ease-in-out;',
    '  transform: translateX(110%);',
    '  opacity: 0;',
    '  display: flex; flex-direction: column;',
    '  padding: 16px;',
    '}',
    '#vj-product-panel.vj-visible {',
    '  transform: translateX(0);',
    '  opacity: 1;',
    '}',
    '#vj-product-panel img,',
    '#vj-product-panel video {',
    '  width: 100%; border-radius: 10px;',
    '  object-fit: cover; max-height: 60%;',
    '}',
    '.vj-product-title {',
    '  font-family: "Inter", sans-serif;',
    '  font-size: 1.1em; font-weight: 700;',
    '  color: #1a1a1a; margin: 10px 0 4px;',
    '}',
    '.vj-product-price {',
    '  font-family: "Inter", sans-serif;',
    '  font-size: 1.3em; font-weight: 800;',
    '  color: #e63946;',
    '}',
    '.vj-product-compare {',
    '  font-size: 0.85em; color: #999;',
    '  text-decoration: line-through; margin-left: 6px;',
    '}',

    // Fullscreen product
    '#vj-product-fullscreen {',
    '  position: absolute; top: 0; left: 0;',
    '  width: 100%; height: 100%;',
    '  background: #000;',
    '  display: flex; align-items: center; justify-content: center;',
    '  opacity: 0; pointer-events: none;',
    '  transition: opacity ' + TRANSITION_MS + 'ms ease-in-out;',
    '}',
    '#vj-product-fullscreen.vj-visible { opacity: 1; }',
    '#vj-product-fullscreen img,',
    '#vj-product-fullscreen video {',
    '  max-width: 90%; max-height: 90%;',
    '  object-fit: contain; border-radius: 8px;',
    '}',

    // Carousel
    '.vj-carousel-track {',
    '  display: flex; gap: 12px;',
    '  animation: vjCarouselScroll 15s linear infinite;',
    '}',
    '@keyframes vjCarouselScroll {',
    '  0%   { transform: translateX(0); }',
    '  100% { transform: translateX(-50%); }',
    '}',

    // L3 Overlays
    '#vj-l3-overlays {',
    '  z-index: ' + (LAYER_Z_BASE + 3) + ';',
    '}',

    // Lower third
    '#vj-lower-third {',
    '  position: absolute; bottom: 8%; left: 0;',
    '  width: 100%; padding: 0 4%;',
    '  box-sizing: border-box;',
    '  transform: translateY(120%);',
    '  opacity: 0;',
    '  transition: all ' + TRANSITION_MS + 'ms cubic-bezier(0.16,1,0.3,1);',
    '}',
    '#vj-lower-third.vj-visible {',
    '  transform: translateY(0); opacity: 1;',
    '}',
    '.vj-lt-bar {',
    '  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);',
    '  border-radius: 12px;',
    '  padding: 14px 20px;',
    '  display: flex; align-items: center; gap: 14px;',
    '  box-shadow: 0 4px 20px rgba(0,0,0,0.3);',
    '}',
    '.vj-lt-name {',
    '  font-family: "Inter", sans-serif;',
    '  font-size: 1.05em; font-weight: 700; color: #fff;',
    '  flex: 1;',
    '}',
    '.vj-lt-price {',
    '  font-family: "Inter", sans-serif;',
    '  font-size: 1.2em; font-weight: 800; color: #f7c948;',
    '  white-space: nowrap;',
    '}',

    // Price tag
    '#vj-price-tag {',
    '  position: absolute; top: 12%; right: 6%;',
    '  background: #e63946; color: #fff;',
    '  font-family: "Inter", sans-serif;',
    '  font-size: 1.4em; font-weight: 800;',
    '  padding: 10px 20px; border-radius: 30px;',
    '  box-shadow: 0 4px 16px rgba(230,57,70,0.4);',
    '  transform: scale(0); opacity: 0;',
    '  transition: all 500ms cubic-bezier(0.34,1.56,0.64,1);',
    '}',
    '#vj-price-tag.vj-visible {',
    '  transform: scale(1); opacity: 1;',
    '}',

    // Deal badge
    '#vj-deal-badge {',
    '  position: absolute; top: 6%; left: 6%;',
    '  background: linear-gradient(135deg, #f7c948, #ff6b35);',
    '  color: #1a1a2e; font-family: "Inter", sans-serif;',
    '  font-size: 1.1em; font-weight: 800;',
    '  padding: 8px 18px; border-radius: 8px;',
    '  transform: rotate(-5deg) scale(0);',
    '  opacity: 0;',
    '  transition: all 500ms cubic-bezier(0.34,1.56,0.64,1);',
    '}',
    '#vj-deal-badge.vj-visible {',
    '  transform: rotate(-5deg) scale(1); opacity: 1;',
    '}',
    '#vj-deal-badge.vj-pulse-badge {',
    '  animation: vjBadgePulse 1.2s ease-in-out infinite;',
    '}',
    '@keyframes vjBadgePulse {',
    '  0%, 100% { transform: rotate(-5deg) scale(1); }',
    '  50%      { transform: rotate(-5deg) scale(1.08); }',
    '}',

    // Special badge (NEW, LIMITED, TRENDING)
    '#vj-special-badge {',
    '  position: absolute; top: 6%; right: 6%;',
    '  font-family: "Inter", sans-serif;',
    '  font-size: 0.85em; font-weight: 700;',
    '  padding: 6px 14px; border-radius: 6px;',
    '  text-transform: uppercase; letter-spacing: 1px;',
    '  transform: scale(0); opacity: 0;',
    '  transition: all 400ms cubic-bezier(0.34,1.56,0.64,1);',
    '}',
    '#vj-special-badge.vj-visible {',
    '  transform: scale(1); opacity: 1;',
    '}',
    '#vj-special-badge.vj-badge-new      { background: #2ec4b6; color: #fff; }',
    '#vj-special-badge.vj-badge-limited   { background: #e63946; color: #fff; }',
    '#vj-special-badge.vj-badge-trending  { background: #7209b7; color: #fff; }',

    // Countdown
    '#vj-countdown {',
    '  position: absolute; bottom: 22%; right: 6%;',
    '  background: rgba(26,26,46,0.92);',
    '  color: #fff; font-family: "Inter", sans-serif;',
    '  border-radius: 14px; padding: 12px 20px;',
    '  text-align: center;',
    '  transform: scale(0); opacity: 0;',
    '  transition: all 500ms cubic-bezier(0.34,1.56,0.64,1);',
    '}',
    '#vj-countdown.vj-visible {',
    '  transform: scale(1); opacity: 1;',
    '}',
    '.vj-cd-label {',
    '  font-size: 0.7em; font-weight: 600;',
    '  text-transform: uppercase; letter-spacing: 1px;',
    '  color: #f7c948; margin-bottom: 4px;',
    '}',
    '.vj-cd-time {',
    '  font-size: 2em; font-weight: 800;',
    '  font-variant-numeric: tabular-nums;',
    '}',
    '#vj-countdown.vj-urgent .vj-cd-time {',
    '  color: #e63946;',
    '  animation: vjCountdownPulse 0.6s ease-in-out infinite alternate;',
    '}',
    '@keyframes vjCountdownPulse {',
    '  0%   { transform: scale(1); }',
    '  100% { transform: scale(1.1); }',
    '}',

    // Event banner
    '#vj-event-banner {',
    '  position: absolute; top: 0; left: 0; width: 100%;',
    '  background: linear-gradient(90deg, #7209b7, #e63946, #f7c948);',
    '  color: #fff; font-family: "Inter", sans-serif;',
    '  font-size: 1em; font-weight: 700;',
    '  text-align: center; padding: 10px 16px;',
    '  transform: translateY(-100%);',
    '  opacity: 0;',
    '  transition: all ' + TRANSITION_MS + 'ms cubic-bezier(0.16,1,0.3,1);',
    '}',
    '#vj-event-banner.vj-visible {',
    '  transform: translateY(0); opacity: 1;',
    '}',

    // Confetti container
    '#vj-confetti {',
    '  position: absolute; top: 0; left: 0;',
    '  width: 100%; height: 100%;',
    '  overflow: hidden; pointer-events: none;',
    '  z-index: ' + (LAYER_Z_BASE + 5) + ';',
    '}',
    '.vj-confetti-piece {',
    '  position: absolute; top: -5%;',
    '  width: 10px; height: 10px;',
    '  border-radius: 2px;',
    '  animation: vjConfettiFall var(--vj-fall-duration, 3s) linear forwards;',
    '}',
    '@keyframes vjConfettiFall {',
    '  0%   { transform: translateY(0) rotate(0deg); opacity: 1; }',
    '  80%  { opacity: 1; }',
    '  100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }',
    '}',

    // L5 Transitions
    '#vj-l5-transitions {',
    '  z-index: ' + (LAYER_Z_BASE + 5) + ';',
    '  pointer-events: none;',
    '}',
    '.vj-trans-fade   { animation: vjTransFade ' + TRANSITION_MS + 'ms ease-in-out forwards; }',
    '.vj-trans-slide-left  { animation: vjTransSlideL ' + TRANSITION_MS + 'ms ease-in-out forwards; }',
    '.vj-trans-slide-right { animation: vjTransSlideR ' + TRANSITION_MS + 'ms ease-in-out forwards; }',
    '.vj-trans-zoom   { animation: vjTransZoom ' + TRANSITION_MS + 'ms ease-in-out forwards; }',
    '.vj-trans-sparkle { animation: vjTransSparkle ' + TRANSITION_MS + 'ms ease-in-out forwards; }',

    '@keyframes vjTransFade {',
    '  0%   { opacity: 0; }',
    '  100% { opacity: 1; }',
    '}',
    '@keyframes vjTransSlideL {',
    '  0%   { transform: translateX(-100%); }',
    '  100% { transform: translateX(0); }',
    '}',
    '@keyframes vjTransSlideR {',
    '  0%   { transform: translateX(100%); }',
    '  100% { transform: translateX(0); }',
    '}',
    '@keyframes vjTransZoom {',
    '  0%   { transform: scale(0.5); opacity: 0; }',
    '  100% { transform: scale(1); opacity: 1; }',
    '}',
    '@keyframes vjTransSparkle {',
    '  0%   { filter: brightness(3); opacity: 0; }',
    '  100% { filter: brightness(1); opacity: 1; }',
    '}',

    // Mobile responsiveness
    '@media (max-width: 768px) {',
    '  #vj-product-panel { width: 55%; }',
    '  .vj-lt-bar { padding: 10px 14px; gap: 8px; }',
    '  .vj-lt-name { font-size: 0.85em; }',
    '  .vj-lt-price { font-size: 1em; }',
    '  #vj-price-tag { font-size: 1.1em; padding: 8px 14px; }',
    '  #vj-deal-badge { font-size: 0.9em; padding: 6px 12px; }',
    '  .vj-cd-time { font-size: 1.4em; }',
    '  #vj-event-banner { font-size: 0.85em; padding: 8px 10px; }',
    '  .vj-product-title { font-size: 0.9em; }',
    '  .vj-product-price { font-size: 1.05em; }',
    '}',
    '@media (max-width: 480px) {',
    '  #vj-product-panel { width: 70%; padding: 10px; }',
    '  .vj-lt-bar { flex-direction: column; align-items: flex-start; gap: 4px; }',
    '  #vj-countdown { bottom: 18%; right: 3%; padding: 8px 14px; }',
    '}'
  ].join('\n');

  function injectCSS() {
    var existing = document.getElementById('vj-compositor-styles');
    if (existing) existing.remove();

    var style = document.createElement('style');
    style.id = 'vj-compositor-styles';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // ---------------------------------------------------------------------------
  // 2. Utility — escaping functions
  // ---------------------------------------------------------------------------

  /**
   * Escape a string for safe insertion as text content within HTML.
   * Uses DOM text node creation to guarantee correctness.
   */
  function escapeHTML(str) {
    if (typeof str !== 'string') str = String(str != null ? str : '');
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.textContent; // Returns the sanitised text
  }

  /**
   * Escape a string for safe use inside an HTML attribute value.
   */
  function escapeAttr(str) {
    return String(str != null ? str : '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function noop() {}

  // ---------------------------------------------------------------------------
  // 3. DOM Builder
  // ---------------------------------------------------------------------------

  var stage = null;
  var els   = {};

  function buildDOM() {
    stage = document.getElementById('clubhouseStage');
    if (!stage) {
      console.warn('[WowVJ] #clubhouseStage not found — searching fallback selectors');
      stage = document.querySelector('.live-stage, .clubhouse-stage, [data-live-stage]');
      if (!stage) return false;
    }

    // L0 — Background
    els.l0 = createLayer('vj-l0-bg');
    stage.appendChild(els.l0);

    // L1 — Lucy video
    els.l1 = createLayer('vj-l1-lucy');
    els.lucyVideo = document.createElement('video');
    els.lucyVideo.id = 'vj-lucy-video';
    els.lucyVideo.autoplay = true;
    els.lucyVideo.loop     = true;
    els.lucyVideo.muted    = true;
    els.lucyVideo.playsInline = true;
    els.lucyVideo.setAttribute('playsinline', '');
    els.lucyVideo.src = LUCY_DEFAULT_CLIP;
    els.l1.appendChild(els.lucyVideo);
    stage.appendChild(els.l1);

    // L2 — Product
    els.l2 = createLayer('vj-l2-product');

    // Side panel
    els.productPanel = document.createElement('div');
    els.productPanel.id = 'vj-product-panel';
    els.l2.appendChild(els.productPanel);

    // Fullscreen
    els.productFs = document.createElement('div');
    els.productFs.id = 'vj-product-fullscreen';
    els.l2.appendChild(els.productFs);

    stage.appendChild(els.l2);

    // L3 — Overlays
    els.l3 = createLayer('vj-l3-overlays');

    // Lower-third — built with DOM methods (no innerHTML)
    els.lowerThird = createEl('div', 'vj-lower-third');
    var ltBar   = document.createElement('div');
    ltBar.className = 'vj-lt-bar';
    var ltName  = document.createElement('span');
    ltName.className = 'vj-lt-name';
    var ltPrice = document.createElement('span');
    ltPrice.className = 'vj-lt-price';
    ltBar.appendChild(ltName);
    ltBar.appendChild(ltPrice);
    els.lowerThird.appendChild(ltBar);
    els.l3.appendChild(els.lowerThird);

    els.priceTag = createEl('div', 'vj-price-tag');
    els.l3.appendChild(els.priceTag);

    els.dealBadge = createEl('div', 'vj-deal-badge');
    els.l3.appendChild(els.dealBadge);

    els.specialBadge = createEl('div', 'vj-special-badge');
    els.l3.appendChild(els.specialBadge);

    // Countdown — built with DOM methods
    els.countdown = createEl('div', 'vj-countdown');
    var cdLabel = document.createElement('div');
    cdLabel.className = 'vj-cd-label';
    cdLabel.textContent = 'Deal ends in';
    var cdTime = document.createElement('div');
    cdTime.className = 'vj-cd-time';
    cdTime.textContent = '00:00';
    els.countdown.appendChild(cdLabel);
    els.countdown.appendChild(cdTime);
    els.l3.appendChild(els.countdown);

    els.eventBanner = createEl('div', 'vj-event-banner');
    els.l3.appendChild(els.eventBanner);

    els.confetti = createEl('div', 'vj-confetti');

    stage.appendChild(els.l3);

    // L5 — Transitions (container for flash overlays)
    els.l5 = createLayer('vj-l5-transitions');
    stage.appendChild(els.l5);

    // Confetti lives above everything
    stage.appendChild(els.confetti);

    return true;
  }

  function createLayer(id) {
    var div = document.createElement('div');
    div.id = id;
    div.className = 'vj-layer';
    return div;
  }

  function createEl(tag, id) {
    var el = document.createElement(tag);
    el.id = id;
    return el;
  }

  // ---------------------------------------------------------------------------
  // 4. State
  // ---------------------------------------------------------------------------

  var state = {
    bg: { type: 'none' },
    lucy: { position: 'center', size: '80%', clip: LUCY_DEFAULT_CLIP, hidden: false },
    product: { display: 'hidden', data: null },
    overlays: {
      lowerThird: false,
      priceTag: false,
      dealBadge: false,
      specialBadge: false,
      countdown: false,
      eventBanner: false
    },
    audio: { musicPlaying: false, musicVolume: 0.3 },
    mood: 'default'
  };

  var countdownTimer = null;
  var carouselTimer  = null;

  // ---------------------------------------------------------------------------
  // 5. Layer Renderers
  // ---------------------------------------------------------------------------

  // --- L0: Background --------------------------------------------------------

  function renderBackground(cfg) {
    if (!cfg) return;
    state.bg = cfg;

    // Remove children and reset
    while (els.l0.firstChild) els.l0.removeChild(els.l0.firstChild);
    els.l0.style.background = '';
    els.l0.classList.remove('vj-pulse');

    switch (cfg.type) {
      case 'gradient':
        els.l0.style.background =
          'linear-gradient(' + (cfg.angle || 135) + 'deg, ' +
          (cfg.colors || ['#1a1a2e', '#16213e']).join(', ') + ')';
        break;

      case 'solid':
        els.l0.style.background = cfg.color || '#1a1a2e';
        break;

      case 'pulse':
        els.l0.style.background =
          'linear-gradient(' + (cfg.angle || 135) + 'deg, ' +
          (cfg.colors || ['#e63946', '#f7c948']).join(', ') + ')';
        els.l0.classList.add('vj-pulse');
        break;

      case 'themed':
        els.l0.style.background =
          cfg.background || 'linear-gradient(135deg, #ff6b6b, #ffa07a)';
        if (cfg.confetti) fireConfetti(cfg.confettiColors);
        break;

      case 'video':
        var vid = document.createElement('video');
        vid.id = 'vj-l0-video';
        vid.autoplay = true;
        vid.loop     = true;
        vid.muted    = true;
        vid.playsInline = true;
        vid.setAttribute('playsinline', '');
        vid.src = cfg.src || '';
        els.l0.appendChild(vid);
        break;

      case 'none':
      default:
        els.l0.style.background = 'transparent';
        break;
    }
  }

  // --- L1: Lucy --------------------------------------------------------------

  function renderLucy(cfg) {
    if (!cfg) return;
    Object.assign(state.lucy, cfg);

    // Visibility
    if (state.lucy.hidden) {
      els.l1.style.opacity = '0';
      els.l1.style.pointerEvents = 'none';
      return;
    }
    els.l1.style.opacity = '1';

    // Position
    els.l1.className = 'vj-layer vj-pos-' + (state.lucy.position || 'center');

    // Size
    var size = state.lucy.size || '80%';
    els.lucyVideo.style.maxWidth  = size;
    els.lucyVideo.style.maxHeight = size;

    // Clip
    if (cfg.clip && cfg.clip !== els.lucyVideo.getAttribute('data-current-clip')) {
      var src = resolveClipURL(cfg.clip);
      els.lucyVideo.setAttribute('data-current-clip', cfg.clip);
      els.lucyVideo.src = src;
      els.lucyVideo.load();
      els.lucyVideo.play().catch(noop);
    }

    // Loop control
    if (typeof cfg.loop === 'boolean') {
      els.lucyVideo.loop = cfg.loop;
    }
  }

  function resolveClipURL(clip) {
    // If already a full URL, use as-is
    if (clip.indexOf('http') === 0) return clip;
    return LUCY_CDN_BASE + clip;
  }

  // --- L2: Product -----------------------------------------------------------

  function renderProduct(cfg) {
    if (!cfg) return;
    state.product = Object.assign(state.product, cfg);

    // Clear carousel
    if (carouselTimer) { clearInterval(carouselTimer); carouselTimer = null; }

    // Hide everything first
    els.productPanel.classList.remove('vj-visible');
    els.productFs.classList.remove('vj-visible');

    var display = cfg.display || 'hidden';

    switch (display) {
      case 'side-panel':
        renderProductPanel(cfg);
        applyTransitionClass(els.productPanel, cfg.animation || 'slide-in-right');
        break;

      case 'fullscreen':
        renderProductFullscreen(cfg, false);
        applyTransitionClass(els.productFs, cfg.animation || 'fade-in');
        break;

      case 'video':
        renderProductFullscreen(cfg, true);
        applyTransitionClass(els.productFs, cfg.animation || 'fade-in');
        break;

      case 'carousel':
        renderProductCarousel(cfg);
        break;

      case 'hidden':
      default:
        break;
    }
  }

  /**
   * Builds the side-panel product card using safe DOM methods.
   */
  function renderProductPanel(cfg) {
    var d = cfg.data || cfg;

    // Clear previous content
    while (els.productPanel.firstChild) els.productPanel.removeChild(els.productPanel.firstChild);

    if (d.image) {
      var img = document.createElement('img');
      img.src = d.image;
      img.alt = escapeHTML(d.title || '');
      els.productPanel.appendChild(img);
    }
    if (d.video) {
      var vid = document.createElement('video');
      vid.autoplay = true;
      vid.loop = true;
      vid.muted = true;
      vid.playsInline = true;
      vid.setAttribute('playsinline', '');
      vid.src = d.video;
      els.productPanel.appendChild(vid);
    }

    var titleDiv = document.createElement('div');
    titleDiv.className = 'vj-product-title';
    titleDiv.textContent = d.title || '';
    els.productPanel.appendChild(titleDiv);

    var priceWrap = document.createElement('div');
    var priceSpan = document.createElement('span');
    priceSpan.className = 'vj-product-price';
    priceSpan.textContent = d.price || '';
    priceWrap.appendChild(priceSpan);

    if (d.comparePrice) {
      var cpSpan = document.createElement('span');
      cpSpan.className = 'vj-product-compare';
      cpSpan.textContent = d.comparePrice;
      priceWrap.appendChild(cpSpan);
    }

    els.productPanel.appendChild(priceWrap);

    // Delay to allow DOM update before triggering transition
    requestAnimationFrame(function () {
      els.productPanel.classList.add('vj-visible');
    });
  }

  /**
   * Builds the fullscreen product display using safe DOM methods.
   */
  function renderProductFullscreen(cfg, isVideo) {
    var d = cfg.data || cfg;

    while (els.productFs.firstChild) els.productFs.removeChild(els.productFs.firstChild);

    if (isVideo && d.video) {
      var vid = document.createElement('video');
      vid.autoplay = true;
      vid.loop = true;
      vid.muted = true;
      vid.playsInline = true;
      vid.setAttribute('playsinline', '');
      vid.src = d.video;
      els.productFs.appendChild(vid);
    } else if (d.image) {
      var img = document.createElement('img');
      img.src = d.image;
      img.alt = escapeHTML(d.title || '');
      els.productFs.appendChild(img);
    }

    requestAnimationFrame(function () {
      els.productFs.classList.add('vj-visible');
    });
  }

  /**
   * Builds the product carousel using safe DOM methods.
   */
  function renderProductCarousel(cfg) {
    var items = cfg.items || [];
    if (!items.length) return;

    while (els.productFs.firstChild) els.productFs.removeChild(els.productFs.firstChild);

    var track = document.createElement('div');
    track.className = 'vj-carousel-track';
    track.style.width = (items.length * 2 * 260) + 'px';

    // Duplicate items for seamless loop
    var allItems = items.concat(items);
    allItems.forEach(function (item) {
      var card = document.createElement('div');
      card.style.cssText =
        'flex-shrink:0; width:240px; background:#fff; border-radius:12px; ' +
        'padding:10px; text-align:center; box-shadow:0 2px 12px rgba(0,0,0,0.1);';

      if (item.image) {
        var img = document.createElement('img');
        img.src = item.image;
        img.alt = escapeHTML(item.title || '');
        img.style.cssText = 'width:100%;border-radius:8px;';
        card.appendChild(img);
      }

      var cardTitle = document.createElement('div');
      cardTitle.className = 'vj-product-title';
      cardTitle.textContent = item.title || '';
      card.appendChild(cardTitle);

      var cardPrice = document.createElement('div');
      cardPrice.className = 'vj-product-price';
      cardPrice.textContent = item.price || '';
      card.appendChild(cardPrice);

      track.appendChild(card);
    });

    els.productFs.style.display = 'flex';
    els.productFs.style.alignItems = 'center';
    els.productFs.style.overflow = 'hidden';
    els.productFs.appendChild(track);
    els.productFs.classList.add('vj-visible');
  }

  // --- L3: Overlays ----------------------------------------------------------

  function renderOverlay(cfg) {
    if (!cfg || !cfg.type) return;

    switch (cfg.type) {
      case 'lowerThird':
        toggleOverlay(els.lowerThird, cfg.visible);
        if (cfg.visible && cfg.data) {
          var nameEl  = els.lowerThird.querySelector('.vj-lt-name');
          var priceEl = els.lowerThird.querySelector('.vj-lt-price');
          if (nameEl)  nameEl.textContent  = cfg.data.title || '';
          if (priceEl) priceEl.textContent = cfg.data.price || '';
        }
        break;

      case 'priceTag':
        toggleOverlay(els.priceTag, cfg.visible);
        if (cfg.visible && cfg.text) {
          els.priceTag.textContent = cfg.text;
        }
        break;

      case 'dealBadge':
        toggleOverlay(els.dealBadge, cfg.visible);
        if (cfg.visible) {
          els.dealBadge.textContent = cfg.text || '';
          if (cfg.pulse) {
            els.dealBadge.classList.add('vj-pulse-badge');
          } else {
            els.dealBadge.classList.remove('vj-pulse-badge');
          }
        }
        break;

      case 'specialBadge':
        // Reset classes first, then apply variant and visibility
        els.specialBadge.className = '';
        if (cfg.visible) {
          els.specialBadge.textContent = cfg.text || cfg.label || '';
          els.specialBadge.className = 'vj-badge-' + (cfg.variant || 'new') + ' vj-visible';
        }
        break;

      case 'countdown':
        if (cfg.visible) {
          startCountdown(cfg.seconds || 0, cfg.label);
        } else {
          stopCountdown();
        }
        break;

      case 'eventBanner':
        toggleOverlay(els.eventBanner, cfg.visible);
        if (cfg.visible) {
          els.eventBanner.textContent = cfg.text || '';
          if (cfg.gradient) {
            els.eventBanner.style.background = cfg.gradient;
          }
        }
        break;

      case 'confetti':
        if (cfg.visible !== false) {
          fireConfetti(cfg.colors, cfg.count);
        }
        break;

      default:
        break;
    }
  }

  function toggleOverlay(el, visible) {
    if (visible) {
      el.classList.add('vj-visible');
    } else {
      el.classList.remove('vj-visible');
    }
  }

  // --- Countdown -------------------------------------------------------------

  function startCountdown(totalSeconds, label) {
    stopCountdown();

    var remaining = totalSeconds;
    var labelEl = els.countdown.querySelector('.vj-cd-label');
    var timeEl  = els.countdown.querySelector('.vj-cd-time');

    if (label && labelEl) labelEl.textContent = label;

    function tick() {
      if (remaining <= 0) {
        stopCountdown();
        return;
      }
      var m = Math.floor(remaining / 60);
      var s = remaining % 60;
      timeEl.textContent = pad2(m) + ':' + pad2(s);

      // Urgency styling when under 30 seconds
      if (remaining <= 30) {
        els.countdown.classList.add('vj-urgent');
      } else {
        els.countdown.classList.remove('vj-urgent');
      }

      remaining--;
    }

    tick();
    els.countdown.classList.add('vj-visible');
    countdownTimer = setInterval(tick, COUNTDOWN_TICK);
  }

  function stopCountdown() {
    if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
    els.countdown.classList.remove('vj-visible', 'vj-urgent');
  }

  function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  // --- Confetti --------------------------------------------------------------

  var CONFETTI_COLORS = ['#e63946','#f7c948','#2ec4b6','#7209b7','#ff6b35','#fff'];

  function fireConfetti(colors, count) {
    var palette = colors || CONFETTI_COLORS;
    var total   = count || CONFETTI_COUNT;

    for (var i = 0; i < total; i++) {
      var piece = document.createElement('div');
      piece.className = 'vj-confetti-piece';
      piece.style.left = Math.random() * 100 + '%';
      piece.style.background = palette[Math.floor(Math.random() * palette.length)];
      piece.style.width  = (6 + Math.random() * 8) + 'px';
      piece.style.height = (6 + Math.random() * 8) + 'px';
      piece.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';
      piece.style.setProperty('--vj-fall-duration', (2 + Math.random() * 2) + 's');
      piece.style.animationDelay = (Math.random() * 1.5) + 's';
      els.confetti.appendChild(piece);
    }

    // Clean up particles after animation completes
    setTimeout(function () {
      while (els.confetti.firstChild) els.confetti.removeChild(els.confetti.firstChild);
    }, 5000);
  }

  // --- Transition helper -----------------------------------------------------

  function applyTransitionClass(el, animName) {
    var map = {
      'slide-in-right': 'vj-trans-slide-right',
      'slide-in-left':  'vj-trans-slide-left',
      'fade-in':        'vj-trans-fade',
      'zoom-in':        'vj-trans-zoom',
      'sparkle':        'vj-trans-sparkle'
    };
    var cls = map[animName] || map['fade-in'];

    el.classList.remove(
      'vj-trans-fade', 'vj-trans-slide-left', 'vj-trans-slide-right',
      'vj-trans-zoom', 'vj-trans-sparkle'
    );
    // Force reflow to restart animation
    void el.offsetWidth;
    el.classList.add(cls);
  }

  // --- L5: Transition flash --------------------------------------------------

  function triggerTransition(cfg) {
    if (!cfg) return;
    var color    = cfg.color || 'rgba(255,255,255,0.9)';
    var duration = cfg.duration || TRANSITION_MS;

    var overlay = document.createElement('div');
    overlay.style.position        = 'absolute';
    overlay.style.top             = '0';
    overlay.style.left            = '0';
    overlay.style.width           = '100%';
    overlay.style.height          = '100%';
    overlay.style.background      = color;
    overlay.style.zIndex          = String(LAYER_Z_BASE + 10);
    overlay.style.pointerEvents   = 'none';
    overlay.style.opacity         = '1';
    overlay.style.transition      = 'opacity ' + duration + 'ms ease-in-out';

    els.l5.appendChild(overlay);

    // Trigger fade-out on next frame
    requestAnimationFrame(function () {
      overlay.style.opacity = '0';
    });

    // Remove element after animation completes
    setTimeout(function () {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, duration + 100);
  }

  // ---------------------------------------------------------------------------
  // 6. Audio Engine (Web Audio API)
  // ---------------------------------------------------------------------------

  var audioCtx    = null;
  var gainMusic   = null;
  var gainVoice   = null;
  var gainSfx     = null;
  var musicSource = null;
  var musicEl     = null;
  var voiceEl     = null;

  var MUSIC_VOLUME_DEFAULT = 0.3;
  var MUSIC_DUCK_VOLUME    = 0.08;
  var DUCK_FADE_MS         = 400;

  function initAudio() {
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;

      audioCtx  = new AC();
      gainMusic = audioCtx.createGain();
      gainVoice = audioCtx.createGain();
      gainSfx   = audioCtx.createGain();

      gainMusic.gain.value = MUSIC_VOLUME_DEFAULT;
      gainVoice.gain.value = 1.0;
      gainSfx.gain.value   = 0.7;

      gainMusic.connect(audioCtx.destination);
      gainVoice.connect(audioCtx.destination);
      gainSfx.connect(audioCtx.destination);
    } catch (e) {
      console.warn('[WowVJ] Audio init failed:', e);
    }
  }

  function ensureAudioCtx() {
    if (audioCtx && audioCtx.state === 'suspended') {
      audioCtx.resume();
    }
  }

  function handleAudio(cfg) {
    if (!cfg) return;
    if (!audioCtx) initAudio();
    if (!audioCtx) return;
    ensureAudioCtx();

    var channel = cfg.channel || 'sfx';

    switch (channel) {
      case 'music':
        handleMusic(cfg);
        break;
      case 'voice':
        handleVoice(cfg);
        break;
      case 'sfx':
        handleSfx(cfg);
        break;
    }
  }

  function handleMusic(cfg) {
    if (cfg.action === 'stop') {
      stopMusic();
      return;
    }

    if (cfg.action === 'volume' && typeof cfg.volume === 'number') {
      state.audio.musicVolume = cfg.volume;
      fadeGain(gainMusic, cfg.volume, DUCK_FADE_MS);
      return;
    }

    if (cfg.action === 'duck') {
      fadeGain(gainMusic, MUSIC_DUCK_VOLUME, DUCK_FADE_MS);
      return;
    }

    if (cfg.action === 'unduck') {
      fadeGain(gainMusic, state.audio.musicVolume, DUCK_FADE_MS);
      return;
    }

    // Play new track
    if (cfg.src) {
      stopMusic();

      musicEl = document.createElement('audio');
      musicEl.crossOrigin = 'anonymous';
      musicEl.loop = cfg.loop !== false;
      musicEl.src  = resolveAudioURL(cfg.src);

      musicSource = audioCtx.createMediaElementSource(musicEl);
      musicSource.connect(gainMusic);

      var vol = typeof cfg.volume === 'number' ? cfg.volume : state.audio.musicVolume;
      state.audio.musicVolume = vol;
      gainMusic.gain.value = 0;
      fadeGain(gainMusic, vol, 1200);

      musicEl.play().catch(noop);
      state.audio.musicPlaying = true;
    }
  }

  function stopMusic() {
    if (musicEl) {
      fadeGain(gainMusic, 0, 600);
      var ref = musicEl;
      setTimeout(function () {
        ref.pause();
        ref.src = '';
      }, 650);
      musicEl = null;
      musicSource = null;
      state.audio.musicPlaying = false;
    }
  }

  function handleVoice(cfg) {
    if (cfg.action === 'stop' && voiceEl) {
      voiceEl.pause();
      voiceEl.src = '';
      voiceEl = null;
      fadeGain(gainMusic, state.audio.musicVolume, DUCK_FADE_MS);
      return;
    }

    if (cfg.src) {
      // Auto-duck music when voice starts
      if (state.audio.musicPlaying) {
        fadeGain(gainMusic, MUSIC_DUCK_VOLUME, DUCK_FADE_MS);
      }

      if (voiceEl) { voiceEl.pause(); voiceEl.src = ''; }

      voiceEl = document.createElement('audio');
      voiceEl.crossOrigin = 'anonymous';
      voiceEl.src = resolveAudioURL(cfg.src);

      var voiceSource = audioCtx.createMediaElementSource(voiceEl);
      voiceSource.connect(gainVoice);

      voiceEl.addEventListener('ended', function () {
        // Unduck music when voice ends
        fadeGain(gainMusic, state.audio.musicVolume, DUCK_FADE_MS);
        voiceEl = null;
      });

      voiceEl.play().catch(noop);
    }
  }

  function handleSfx(cfg) {
    var src = cfg.src || cfg.name;
    if (!src) return;

    // Resolve preset name to filename
    if (SFX_MAP[src]) src = SFX_MAP[src];

    var audio = document.createElement('audio');
    audio.crossOrigin = 'anonymous';
    audio.src = resolveAudioURL(src);

    var sfxSource = audioCtx.createMediaElementSource(audio);
    sfxSource.connect(gainSfx);

    audio.play().catch(noop);

    // Clean up after playback
    audio.addEventListener('ended', function () {
      audio.src = '';
    });
  }

  function fadeGain(gainNode, target, durationMs) {
    if (!gainNode || !audioCtx) return;
    var now = audioCtx.currentTime;
    gainNode.gain.cancelScheduledValues(now);
    gainNode.gain.setValueAtTime(gainNode.gain.value, now);
    gainNode.gain.linearRampToValueAtTime(target, now + durationMs / 1000);
  }

  function resolveAudioURL(src) {
    if (src.indexOf('http') === 0) return src;
    return AUDIO_CDN_BASE + src;
  }

  // ---------------------------------------------------------------------------
  // 7. Mood Engine
  // ---------------------------------------------------------------------------

  var MOOD_PRESETS = {
    default: {
      bg: { type: 'gradient', colors: ['#1a1a2e', '#16213e'], angle: 135 }
    },
    energetic: {
      bg: { type: 'gradient', colors: ['#e63946', '#f7c948'], angle: 135 }
    },
    calm: {
      bg: { type: 'gradient', colors: ['#2ec4b6', '#3d5a80'], angle: 135 }
    },
    luxury: {
      bg: { type: 'gradient', colors: ['#1a1a2e', '#3d0066'], angle: 135 }
    },
    sale: {
      bg: { type: 'pulse', colors: ['#e63946', '#ff6b35'], angle: 135 }
    },
    party: {
      bg: { type: 'gradient', colors: ['#7209b7', '#e63946', '#f7c948'], angle: 135 }
    },
    nature: {
      bg: { type: 'gradient', colors: ['#2d6a4f', '#40916c', '#95d5b2'], angle: 135 }
    }
  };

  function applyMood(cfg) {
    var name   = (typeof cfg === 'string') ? cfg : (cfg.name || 'default');
    var preset = MOOD_PRESETS[name] || MOOD_PRESETS['default'];

    state.mood = name;
    renderBackground(preset.bg);

    if (name === 'party') {
      fireConfetti();
    }
  }

  // ---------------------------------------------------------------------------
  // 8. Preset Engine (apply full scene at once)
  // ---------------------------------------------------------------------------

  function applyPreset(data) {
    if (!data) return;

    // Optional transition flash before applying the new scene
    if (data.transition) {
      triggerTransition(data.transition);
    }

    var delay = data.transition ? 200 : 0;

    setTimeout(function () {
      if (data.bg)       renderBackground(data.bg);
      if (data.lucy)     renderLucy(data.lucy);
      if (data.product)  renderProduct(data.product);
      if (data.mood)     applyMood(data.mood);

      if (data.overlays && Array.isArray(data.overlays)) {
        data.overlays.forEach(function (o) { renderOverlay(o); });
      }

      if (data.audio) handleAudio(data.audio);
    }, delay);
  }

  // ---------------------------------------------------------------------------
  // 9. Full State Sync
  // ---------------------------------------------------------------------------

  function applyFullState(data) {
    if (!data) return;

    if (data.bg)      renderBackground(data.bg);
    if (data.lucy)    renderLucy(data.lucy);
    if (data.product) renderProduct(data.product);
    if (data.mood)    applyMood(data.mood);

    if (data.overlays) {
      Object.keys(data.overlays).forEach(function (type) {
        var oCfg = data.overlays[type];
        renderOverlay({
          type:    type,
          visible: oCfg.visible,
          data:    oCfg,
          text:    oCfg.text,
          label:   oCfg.label,
          variant: oCfg.variant,
          pulse:   oCfg.pulse,
          seconds: oCfg.seconds,
          gradient:oCfg.gradient,
          colors:  oCfg.colors,
          count:   oCfg.count
        });
      });
    }

    if (data.audio) handleAudio(data.audio);
  }

  // ---------------------------------------------------------------------------
  // 10. Badge Handler
  // ---------------------------------------------------------------------------

  function handleBadge(cfg) {
    if (!cfg) return;
    renderOverlay({
      type:    'specialBadge',
      visible: cfg.visible !== false,
      text:    cfg.text || cfg.label || '',
      variant: cfg.variant || 'new'
    });
  }

  // ---------------------------------------------------------------------------
  // 11. Socket.io Event Binding
  // ---------------------------------------------------------------------------

  var socket       = null;
  var socketBound  = false;
  var bindRetries  = 0;
  var MAX_RETRIES  = 15;

  function bindSocket() {
    if (socketBound) return;

    // Find existing socket from WowLiveEngine or LucyClubhouse
    if (window.WowLiveEngine && window.WowLiveEngine.socket) {
      socket = window.WowLiveEngine.socket;
    } else if (window.LucyClubhouse && window.LucyClubhouse.socket) {
      socket = window.LucyClubhouse.socket;
    } else {
      bindRetries++;
      if (bindRetries <= MAX_RETRIES) {
        console.warn(
          '[WowVJ] No Socket.io connection found — retry ' + bindRetries + '/' + MAX_RETRIES
        );
        setTimeout(bindSocket, 2000);
      } else {
        console.error('[WowVJ] Could not find Socket.io after ' + MAX_RETRIES + ' retries');
      }
      return;
    }

    console.log('[WowVJ] Bound to existing Socket.io connection');
    socketBound = true;

    socket.on('vj:preset',     applyPreset);
    socket.on('vj:layer',      handleLayerEvent);
    socket.on('vj:product',    renderProduct);
    socket.on('vj:overlay',    renderOverlay);
    socket.on('vj:transition', triggerTransition);
    socket.on('vj:audio',      handleAudio);
    socket.on('vj:mood',       applyMood);
    socket.on('vj:badge',      handleBadge);
    socket.on('vj:state',      applyFullState);
  }

  /**
   * Generic layer event handler. Routes to the right renderer
   * based on data.layer (0-5) or data.name.
   */
  function handleLayerEvent(data) {
    if (!data) return;
    var layer = data.layer != null ? data.layer : layerNameToIndex(data.name);

    switch (layer) {
      case 0: renderBackground(data.config || data); break;
      case 1: renderLucy(data.config || data);       break;
      case 2: renderProduct(data.config || data);     break;
      case 3: renderOverlay(data.config || data);     break;
      case 5: triggerTransition(data.config || data); break;
      default: break;
    }
  }

  function layerNameToIndex(name) {
    var map = {
      bg: 0, background: 0,
      lucy: 1,
      product: 2,
      overlay: 3, overlays: 3,
      interactive: 4,
      transition: 5, transitions: 5
    };
    return map[name] != null ? map[name] : -1;
  }

  // ---------------------------------------------------------------------------
  // 12. Public API
  // ---------------------------------------------------------------------------

  var WowVJ = {
    version: '1.0.0',

    // --- Direct layer control ---
    setBackground: renderBackground,
    setLucy:       renderLucy,
    setProduct:    renderProduct,
    setOverlay:    renderOverlay,
    setMood:       applyMood,

    // --- Scene management ---
    applyPreset:   applyPreset,
    syncState:     applyFullState,

    // --- Transitions and effects ---
    transition:    triggerTransition,
    confetti:      fireConfetti,

    // --- Audio ---
    audio:         handleAudio,

    playMusic: function (src, opts) {
      handleAudio(Object.assign({ channel: 'music', src: src }, opts || {}));
    },

    stopMusic: stopMusic,

    playSfx: function (name) {
      handleAudio({ channel: 'sfx', name: name });
    },

    playVoice: function (src) {
      handleAudio({ channel: 'voice', src: src });
    },

    // --- Overlay shortcuts ---

    showBadge: function (text, variant) {
      handleBadge({ visible: true, text: text, variant: variant || 'new' });
    },
    hideBadge: function () {
      handleBadge({ visible: false });
    },

    startCountdown: function (seconds, label) {
      renderOverlay({ type: 'countdown', visible: true, seconds: seconds, label: label });
    },
    stopCountdown: stopCountdown,

    showLowerThird: function (title, price) {
      renderOverlay({ type: 'lowerThird', visible: true, data: { title: title, price: price } });
    },
    hideLowerThird: function () {
      renderOverlay({ type: 'lowerThird', visible: false });
    },

    showDeal: function (text, pulse) {
      renderOverlay({ type: 'dealBadge', visible: true, text: text, pulse: pulse !== false });
    },
    hideDeal: function () {
      renderOverlay({ type: 'dealBadge', visible: false });
    },

    showPrice: function (text) {
      renderOverlay({ type: 'priceTag', visible: true, text: text });
    },
    hidePrice: function () {
      renderOverlay({ type: 'priceTag', visible: false });
    },

    showBanner: function (text, gradient) {
      renderOverlay({ type: 'eventBanner', visible: true, text: text, gradient: gradient });
    },
    hideBanner: function () {
      renderOverlay({ type: 'eventBanner', visible: false });
    },

    // --- Bulk operations ---

    hideAll: function () {
      renderOverlay({ type: 'lowerThird',   visible: false });
      renderOverlay({ type: 'priceTag',     visible: false });
      renderOverlay({ type: 'dealBadge',    visible: false });
      renderOverlay({ type: 'specialBadge', visible: false });
      renderOverlay({ type: 'countdown',    visible: false });
      renderOverlay({ type: 'eventBanner',  visible: false });
      renderProduct({ display: 'hidden' });
    },

    // --- State inspection ---
    getState: function () { return JSON.parse(JSON.stringify(state)); },

    // --- Internal references (debugging) ---
    _els:   els,
    _stage: function () { return stage; }
  };

  // ---------------------------------------------------------------------------
  // 13. Init
  // ---------------------------------------------------------------------------

  function init() {
    // Prevent double initialization
    if (window.WowVJ) {
      console.log('[WowVJ] Already initialized');
      return;
    }

    injectCSS();

    var domReady = buildDOM();
    if (!domReady) {
      console.error('[WowVJ] Failed to build DOM — #clubhouseStage not found');
      // Expose API anyway so it can be used once the stage element exists
      window.WowVJ = WowVJ;
      return;
    }

    initAudio();
    bindSocket();

    // Apply transparent background as default
    renderBackground({ type: 'none' });

    window.WowVJ = WowVJ;
    console.log('[WowVJ] VJ Compositor v' + WowVJ.version + ' initialized');
  }

  // Boot when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
